import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import ClearIcon from '@material-ui/icons/Clear';
import CheckIcon from '@material-ui/icons/Check';
import PerfectScrollbar from 'react-perfect-scrollbar';
import CloseIcon from '@material-ui/icons/Close';

import { LinkComponent, LoaderBar, Page } from 'src/components';
import { Branch, PermissionApiName, Role } from 'src/types';

import { UserBasicInfo } from 'src/types/user';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { multiBranchFeat } from 'src/constants/feature-toggle';
import { batch } from 'react-redux';
import useResolution from 'src/hooks/useResolution';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  clearText: {
    color: 'rgb(244, 67, 54)',
    display: 'flex',
    justifyContent: 'space-between'
  },
  statusContainer: {
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  usernameCell: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '250px'
  }
}));

const { actions: userActions, selectors: userSelectors } = slices.user;
const { actions: roleActions, selectors: roleSelectors } = slices.roles;
const {
  actions: permissionActions,
  selectors: permissionSelectors
} = slices.permissions;

const UsersView = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const users = useAppSelector(userSelectors?.selectAllUsers);
  const roles = useAppSelector(roleSelectors.selectRoleList);
  const permissions = useAppSelector(permissionSelectors.selectPermissionList);
  const { isMobile } = useResolution();

  const [isUsersLoading, setUsersLoading] = useState<boolean>(false);
  const [filteredUsers, setFilteredUsers] = useState<UserBasicInfo[]>([]);
  const [isRolesLoading, setRolesLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPermissionsLoading, setPermissionsLoading] = useState<boolean>(
    false
  );
  const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserBasicInfo | null>(null);
  //   filters state
  const [fullNameVal, setFullNameVal] = useState<string>('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedPerms, setSelectedPerms] = useState<PermissionApiName[]>([]);

  const onChangeRoles = useCallback((val: any) => {
    if (val.includes('clear')) {
      setSelectedRoles([]);
      return;
    }
    setSelectedRoles(val);
  }, []);

  const handleOpenPermissionDialog = (user: UserBasicInfo) => {
    setSelectedUser(user);
    setOpenPermissionDialog(true);
  };

  const handleClosePermissionDialog = () => {
    setSelectedUser(null);
    setOpenPermissionDialog(false);
  };

  const permsChecker = useCallback(
    (user?: UserBasicInfo) => {
      const userPerms = user?.all_permissions?.map((i) => i.name);
      return selectedPerms.every((i) => userPerms?.includes(i));
    },
    [selectedPerms]
  );

  const roleChecker = useCallback(
    (user?: UserBasicInfo) => {
      const userRoles = user?.all_roles?.map((i) => i.name);
      return selectedRoles.every((i) => userRoles?.includes(i));
    },
    [selectedRoles]
  );

  const onApplyFilter = () => {
    setIsLoading(true);
    if (users) {
      const results = users.filter((user) => {
        const fullName = fullNameVal.toLowerCase().split(' ');

        const userNameMatch = fullName.every((i) =>
          user?.username?.toLowerCase().includes(i)
        );

        const fullNameMatch = fullName.every((word) =>
          `${user.first_name} ${user.last_name}`.toLowerCase().includes(word)
        );

        const rolesMatch = roleChecker(user);
        const permsMatch = permsChecker(user);

        return (userNameMatch || fullNameMatch) && rolesMatch && permsMatch;
      });

      setFilteredUsers(results);
    }
    setIsLoading(false);
  };

  const onPressClear = useCallback(() => {
    setFullNameVal('');
    setSelectedPerms([]);
    setSelectedRoles([]);
  }, []);

  const onChangePerms = useCallback((val: any) => {
    if (val.includes('clear')) {
      setSelectedPerms([]);
      return;
    }
    setSelectedPerms(val);
  }, []);

  const rolesOfAUser = useCallback(
    (rolesArg: Role[]) => rolesArg?.map((x) => x.name).join(', '),
    []
  );

  const branchOfAUser = useCallback(
    (branchArg: Branch[]) => branchArg?.map((x) => x.branch_name).join(', '),
    []
  );

  const onFullNameInputChange = (val: string) => {
    setFullNameVal(val);
  };

  const getRolesAndPermissions = useCallback(() => {
    batch(() => {
      setRolesLoading(true);
      dispatch(roleActions.getRolesThunk()).finally(() =>
        setRolesLoading(false)
      );

      setPermissionsLoading(true);
      dispatch(permissionActions.getPermissionsThunk()).finally(() =>
        setPermissionsLoading(false)
      );
    });
  }, [dispatch]);

  const getAllUser = useCallback(async () => {
    setUsersLoading(true);
    dispatch(userActions.getAllUsersThunk()).finally(() =>
      setUsersLoading(false)
    );
  }, [dispatch]);

  const isUserActive = (user: UserBasicInfo) => {
    return user?.is_active === 1 || user?.is_active === null;
  };

  useEffect(() => {
    getAllUser();
    getRolesAndPermissions();
  }, [getAllUser, getRolesAndPermissions]);

  useEffect(() => {
    if (users && users[0]) {
      setFilteredUsers(users);
    }
  }, [users]);

  return (
    <Page className={classes.root} title="Manage Users">
      <Container maxWidth={false}>
        <Typography color="textPrimary" gutterBottom variant="h3">
          Users / Employees
        </Typography>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          style={{ marginTop: '40px' }}
        >
          <Grid container spacing={2} justifyContent="space-between">
            <Grid item xs={12} md={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <FormControl variant="outlined" style={{ width: '100%' }}>
                    <TextField
                      value={fullNameVal}
                      variant="outlined"
                      onKeyDown={(e) => {
                        e.key === 'Enter' && onApplyFilter();
                      }}
                      placeholder="Search Name"
                      onChange={(e) => {
                        const val = e.target.value;
                        onFullNameInputChange(val);
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl variant="outlined" style={{ width: '100%' }}>
                    <InputLabel>Select Roles</InputLabel>
                    <Select
                      value={selectedRoles}
                      label="Select Roles"
                      multiple
                      disabled={isRolesLoading}
                      placeholder="Select Roles"
                      onChange={(e) => onChangeRoles(e.target.value)}
                      renderValue={(selected: any) => (
                        <Typography>{selected.join(', ')}</Typography>
                      )}
                    >
                      <MenuItem value={'clear'} className={classes.clearText}>
                        Clear Roles
                      </MenuItem>
                      {roles &&
                        roles[0] &&
                        roles.map((i, ind) => (
                          <MenuItem
                            className={classes.menuItem}
                            key={ind}
                            value={i.name}
                          >
                            {i.name}
                            {i?.name && selectedRoles.includes(i?.name) && (
                              <CheckIcon />
                            )}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl variant="outlined" style={{ width: '100%' }}>
                    <InputLabel>Select Perms</InputLabel>
                    <Select
                      value={selectedPerms}
                      multiple
                      label="Select Perms"
                      disabled={isPermissionsLoading}
                      onChange={(e) => onChangePerms(e.target.value)}
                      renderValue={(selected: any) => (
                        <Typography>{selected.join(',')}</Typography>
                      )}
                    >
                      <MenuItem value={'clear'} className={classes.clearText}>
                        Clear Perms
                        <ClearIcon color="secondary" />
                      </MenuItem>
                      {permissions &&
                        permissions[0] &&
                        permissions.map((i, ind) => (
                          <MenuItem
                            className={classes.menuItem}
                            key={ind}
                            value={i.name}
                          >
                            {i.name}
                            {i?.name && selectedPerms.includes(i?.name) && (
                              <CheckIcon />
                            )}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onApplyFilter}
                    disabled={isLoading}
                    style={{ height: '56px', width: '100%' }}
                  >
                    Apply Filter
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    endIcon={<ClearIcon />}
                    onClick={onPressClear}
                    color="secondary"
                    style={{ height: '56px', width: '100%' }}
                  >
                    Set To Default
                  </Button>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    onClick={() => navigate('/app/user/add')}
                    color="primary"
                    variant="contained"
                    style={{ height: '56px', width: '100%' }}
                  >
                    Create new user
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box marginY={2}>
          <Card>
            <Divider />
            <PerfectScrollbar>
              <Box>
                <LoaderBar isLoading={isUsersLoading} />
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee Full Name</TableCell>
                      <TableCell>Active Status</TableCell>
                      <TableCell>UserName</TableCell>
                      <TableCell>Roles</TableCell>
                      {multiBranchFeat ? <TableCell>Branches</TableCell> : null}
                      <TableCell>Permissions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers?.map((item: UserBasicInfo) => {
                      const active = isUserActive(item);

                      return (
                        <TableRow hover key={item.id}>
                          <TableCell width={300}>
                            <LinkComponent
                              href={`/app/user/${item.id}`}
                              onClick={() => navigate(`/app/user/${item.id}`)}
                              title={`${item?.first_name ||
                                ''} ${item?.last_name || ''}`}
                            />
                          </TableCell>
                          <TableCell width={200}>
                            <Chip
                              variant="outlined"
                              label={active ? 'Active' : 'Inactive'}
                              style={{
                                backgroundColor: active ? '#e7f9ed' : '#F9E7E7',
                                color: active ? '#0b7724' : '#770B0D'
                              }}
                            />
                          </TableCell>
                          <TableCell className={classes.usernameCell}>
                            {item?.username || '--'}
                          </TableCell>
                          <TableCell>
                            {rolesOfAUser(item?.all_roles || [])}
                          </TableCell>
                          {multiBranchFeat ? (
                            <TableCell>
                              {branchOfAUser(item?.branches || [])}
                            </TableCell>
                          ) : null}
                          <TableCell>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleOpenPermissionDialog(item)}
                            >
                              Show Permission
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </PerfectScrollbar>
          </Card>
        </Box>
      </Container>

      <Dialog
        open={openPermissionDialog}
        onClose={handleClosePermissionDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          User Permissions
          <IconButton
            aria-label="close"
            onClick={handleClosePermissionDialog}
            style={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedUser?.all_permissions &&
          selectedUser.all_permissions.length > 0 ? (
            isMobile ? (
              <Box>
                {selectedUser.all_permissions.map((perm, index) => (
                  <Card
                    key={index}
                    style={{ marginBottom: '1rem', padding: '1rem' }}
                  >
                    <Typography variant="h6" style={{ wordWrap: 'break-word' }}>
                      {perm.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {perm.description || '—'}
                    </Typography>
                  </Card>
                ))}
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Permission Name</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedUser.all_permissions.map((perm, index) => (
                    <TableRow key={index}>
                      <TableCell>{perm.name}</TableCell>
                      <TableCell width={500}>
                        {perm.description || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
            <Typography>No permissions assigned.</Typography>
          )}
        </DialogContent>
      </Dialog>
    </Page>
  );
};

export default UsersView;
